/**
 * These methods manage pages to be displayed within the application process.
 * The idea is to get the next page based on the current state of the application data.
 * The "PageStack" is kept in the mygaSession so that when the session is cleared the
 * stack is reset
 */
import {isValid, mygaSession} from "./myga-session";
import {PAGES, STATE, STORAGE_KEYS} from "./constants";
import {NassauLog} from "./myga-utils";
import {
  hasAnnuitant,
  hasOwner,
  isCD, isFinancesComplete, isKnockoutFinances,
  isPathB, isPathC,
  isRecommendation, isReplacement,
  isReplacementOrCD, isUsingQualifiedFunds, willStopPayments,
  getConditionalValue,
  isConditionValid
} from "./validation";
import {QUESTIONS} from "./nfg-myga";
import {getPageFlowFromProductType} from "./product-manager";
import pages from "./myga-ui";

const mygaPageManagerData = () => {
  return window.mygaPageManagerData = window.mygaPageManagerData || {};
}

const PAGES_NON_APP = [
    PAGES.pageLanding,
    PAGES.pageDashboard,
    PAGES.pageResearchCenter
];

export class PageManager {
  
  static registerPageFactory = (page, factory) => {
    mygaPageManagerData()[page] = factory;
  }
  
  static pageFactory = (page) => {
    return mygaPageManagerData()[page];
  }
  
  /**
   * Set the current page ID
   * @param page
   * @param returnToPage
   * @param showCancel
   */
  static setCurrentPage = (page,returnToPage,showCancel = false) => {
    // NassauLog(`PageManager.setCurrentPage: ${page}, returnToPage: ${returnToPage}, showCancel: ${showCancel}`);
    
    // set the current page in the session
    mygaSession.setMygaSessionValue(STATE.page, page);
    mygaSession.setMygaSessionValue(STORAGE_KEYS.CurrPage, page);
  
    // get the page stack
    let pageStack = mygaSession.getMygaSessionValue(STORAGE_KEYS.PageStack,[]);
  
    // Check to see if we have an active returnToPage defined and if so clear it
    // NassauLog(`mygaSession.getReturnToPage: ${mygaSession.getReturnToPage()}`);
    if (isValid(mygaSession.getReturnToPage())) {
      // NassauLog('mygaSession.clearReturnToPage');
      mygaSession.clearReturnToPage();
    }
  
    // check for a returnToPage, this means we are using the page like a modal or quick return
    if (isValid(returnToPage)) {
      mygaSession.setReturnToPage(returnToPage);
      mygaSession.setShowCancel(showCancel);
    }
    
    // Check for a non-app page. if so, clear the page stack.
    else if(PAGES_NON_APP.indexOf(page)>=0) {
      if (pageStack.length>0) {
        mygaSession.setMygaSessionValue(STORAGE_KEYS.PrevPageStack,pageStack);
      }
      mygaSession.setMygaSessionValue(STORAGE_KEYS.PageStack,[]);
    }
  
    // If we are not returning to a page
    // check the last page that was pushed and if the same as current skip
    // otherwise push the page onto the pageStack
    else  if ((pageStack.length===0) || (pageStack[pageStack.length-1] !== page)) {
      // NassauLog(`PageManager: pageStack.push ${page}`);
  
      // the page is different than the last one pushed so push
      pageStack.push(page);
      // save the updated stack
      mygaSession.setMygaSessionValue(STORAGE_KEYS.PageStack,pageStack);
    }
    
  }

  static getCurrentPage = () => {
    return mygaSession.getMygaSessionValue(STORAGE_KEYS.CurrPage, '');
  }

  static setFirstPageOfTest = page => {
    mygaSession.setMygaSessionValue(STORAGE_KEYS.FirstPageOfTest, page);
  }

  static getFirstPageOfTest = () => {
    return mygaSession.getMygaSessionValue(STORAGE_KEYS.FirstPageOfTest, PAGES.pageLanding);
  }
  
  // 2021.02.08.ADivelbiss - Added support for validating routes.
  // Certain data are required to be present for route navigation
  // to succeed.  This function returns the "nearest" valid page.
  static validateDataForPage = (page) => {

    // NassauLog(`validateDataForPage: ${page}`);

    // Get the page prerequisite object
    let pagePrerequisite = getPageFlowFromProductType()[page];
    if (!isValid(pagePrerequisite)) {
      NassauLog(`validateDataForPage: ${page} pagePrerequisite not found`);
      return page;
    }

    // there are application requirements to check for a principal
    // handle required principal by returning to the Dashboard
    if (mygaSession.getPrincipal()===null && page !== PAGES.pageDashboard) {
      return PAGES.pageGuestEmail; // PAGES.pageDashboard; 2021.08.03.ADivelbiss - go to email page instead
    }

    // If there is no condition then it is met so return
    if (!isValid(pagePrerequisite.condition)) {
      NassauLog(`validateDataForPage: ${page} pagePrerequisite.condition not found`);
      return page;
    }

    // there is a condition so check if it is met
    if (isConditionValid(pagePrerequisite.condition)) {
      // NassauLog(`validateDataForPage: ${page} is valid`);
      return page;
    }
    // NassauLog(`validateDataForPage: ${page} is NOT valid`);

    // Now get the previous page in the flow
    // let prevPage = pagePrerequisite.prevPage;
    // 2022.04.01.ADivelbiss - Added code to get the previous page from the condition
    let prevPage = getConditionalValue(pagePrerequisite.prevPage);
    // NassauLog('prevPage: '+prevPage);

    // Return the condition for the previous page
    return PageManager.validateDataForPage(prevPage);

  }; // validateDataForPage

  /**
   * Returns the previous page ID
   * Does not pop it from the stack
   * ASSERT: assume this is called after setCurrentPage. i.e, top of the stack is current page
   * @returns {*|string}
   */
  static getPreviousPage = () => {
    
    // Handle pages with no previous button.
    const currPage = mygaSession.getMygaSessionValue(STORAGE_KEYS.CurrPage);
    if (
        currPage === PAGES.pageAppKnockoutLanding ||
        currPage === PAGES.pageAppCompleted ||
        currPage === PAGES.pageAppPartialComplete ||
        currPage === PAGES.pageAppPartialCompleteAR
    ) {
      return null;
    }
    
    // Handle pages with a "return to" page
    const returnToPage = mygaSession.getReturnToPage();
    const showCancel = mygaSession.getShowCancel();
    // NassauLog(`PageManager.getPreviousPage: returnToPage: ${returnToPage}, showCancel: ${showCancel}`);
    if (isValid(returnToPage)) {
      return (showCancel) ? returnToPage : null;
      // return returnToPage;
      // return null;
    }
  
    // Handle the normal case using the page stack.
    let pageStack = mygaSession.getMygaSessionValue(STORAGE_KEYS.PageStack,[]);
    let prevPage = PAGES.pageDashboard; // minimum page is the dashboard
    if (pageStack.length>1) {
      prevPage = pageStack[pageStack.length-2];
    }
    // NassauLog(`PageManager.getPreviousPage: ${prevPage}`);
    return prevPage;
  }
  
  /**
   * Pops the current page from the stack and returns the current page ID
   * @returns {string}
   */
  static popCurrentPage = () => {
    let pageStack = mygaSession.getMygaSessionValue(STORAGE_KEYS.PageStack,[]);
    let page = PAGES.pageDashboard; // minimum page is the dashboard
    if (pageStack.length>0) {
      // pop the page
      page = pageStack.pop();
      // save the updated stack
      mygaSession.setMygaSessionValue(STORAGE_KEYS.PageStack,pageStack);
    }
    return page;
  }
  
  /**
   * Returns the next page in the workflow based on mygaSession state.
   * Initially this method is "canned" but provides an abstraction that
   * will enable us to develop a more sophisticated algorithm later.
   */
  static getNextPage = () => {
    // Check to see if a "return to" page as specified.
    // Currently implemented in the Finalize page to allow users to "go back" and edit information.
    // Handle returnToPage case
    const returnToPage = mygaSession.getReturnToPage();
    if (isValid(returnToPage)) {
      // NassauLog(`getNextPage: returnToPage: ${returnToPage}`);
      return returnToPage;
    }
    
    // set the default next page
    let nextPage = PAGES.pageDashboard;
    
    // const appStateAbbr = mygaSession.getMygaSessionValue(STORAGE_KEYS.AppStateAbbr,'');
    const currPage = mygaSession.getMygaSessionValue(STORAGE_KEYS.CurrPage);
  
    let cv = getConditionalValue(getPageFlowFromProductType()[currPage]?.nextPage);
    // NassauLog('nextPage: '+cv)
    return cv ? cv : nextPage;
  }
  
  static getQuestions = () => {
    // TODO: Handle question set based on product provider
    return QUESTIONS;
  }
  
  static getQuestionByName = (name) => {
    const questions = PageManager.getQuestions();
    const index = questions.findIndex(q=>q.name===name);
    if (index<0) return null;
    return questions[index];
  }
  
  // Returns the last page with completed data
  static lastCompletedPage = () => {
    return PageManager.validateDataForPage(PAGES.pageAppSubmitApp);
  };

  // Returns true if the application has been started otherwise false
  static isApplicationStarted = () => {
    return (PageManager.lastCompletedPage()!==PAGES.pageDashboard);
  };

  static isPageCompleted = (page) => {
    return (PageManager.validateDataForPage(page)===page);
  };

  // Returns true if the application is finished.
  static isApplicationFinished = () => {
    return PageManager.isPageCompleted(PAGES.pageAppSubmitApp);
  };

  static getPageCondition = (condition) => {

    let result = null;

    // check for a valid condition
    if (!isValid(condition)) {
      return null;
    }

    // check for a page condition
    if (isValid(condition.page)) {
      result = condition.page;
    }

    else if (isValid(condition.AND) && condition.AND.length>0) {
      let arr = condition.AND.filter(c => {
        return isValid(c.page) || isValid(c.AND) || isValid(c.OR);
      });
      for (let i = 0; !isValid(result) && i < arr.length; i++) {
        result = PageManager.getPageCondition(arr[i]);
      }
    }

    // check for OR
    if (isValid(condition.OR) && condition.OR.length>0) {
      let arr = condition.OR.filter(c => {
        return isValid(c.page) || isValid(c.AND) || isValid(c.OR);
      });
      for (let i = 0; !isValid(result) && i < arr.length; i++) {
        result = PageManager.getPageCondition(arr[i]);
      }
    }

    return result;
  }

  static getPageDepth = (page,depth=0) => {
    const pagePrerequisite = getPageFlowFromProductType()[page];
    if (!isValid(pagePrerequisite)) {
      return depth;
    }

    const nextPage = PageManager.getPageCondition(pagePrerequisite.condition);
    if (!isValid(nextPage)) {
      return depth+1;
    }

    return PageManager.getPageDepth(nextPage, depth+1);
  }

  /**
   * Compares the depth of two pages.
   * Page depth is how far along the page is in the applicaiton process
   * if p1 is deeper than p2 the return value is > 0
   * if p2 is not as deep as p2 the return value is < 0
   * if p1 and p2 are the same depth then return value is = 0
   * @param p1
   * @param p2
   * @returns {number}
   */
  static comparePageDepth = (p1, p2) => {
    let d1 = PageManager.getPageDepth(p1);
    let d2 = PageManager.getPageDepth(p2);
    // NassauLog(`${p1}:${d1} ?= ${p2}:${d2}`);
    return (d1 - d2);
  }
  
} // class PageManager

